<template>
  <div class="h-full">
    <div
      v-if="record"
      class="bg-back-2 rounded-[8px] p-[8px_8px_8px_24px] mb-[16px] flex gap-[8px] flex-nowrap"
    >
      <div class="py-[16px] text-14 flex flex-wrap gap-y-[10px] max-w-[416px]">
        <div class="w-1/2 flex items-start flex-col text-font-1">
          <span class="font-medium">{{ $t('cheng-yuan-xing-ming') }}：</span>
          {{ record?.account_name }}
        </div>
        <div class="w-1/2 flex items-start flex-col text-font-1">
          <span class="font-medium">{{ $t('jiao-se') }}：</span>
          {{ useDictItem('roles', record?.permission)?.label }}
        </div>
        <div
          v-if="record?.phone_number && !isGlobalVersion()"
          class="w-1/2 flex items-start flex-col text-font-1"
        >
          <span class="font-medium">{{ $t('dian-hua-hao-ma') }}：</span>
          {{ record?.phone_number }}
        </div>
        <div
          v-if="record?.email_address && isGlobalVersion()"
          class="w-1/2 flex items-start flex-col text-font-1"
        >
          <span class="font-medium">{{ $t('bang-ding-you-xiang') }}：</span>
          {{ record?.email_address }}
        </div>
        <div class="w-1/2 flex items-start flex-col text-font-1">
          <span class="font-medium">{{ $t('jia-ru-shi-jian') }}：</span>
          {{ timeFormat(record?.create_time, 'YYYY.MM.DD') }}
        </div>
      </div>
      <div
        class="coin p-[16px_30px_12px_30px] bg-back-1 rounded-[8px] flex flex-col justify-between max-w-[200px]"
      >
        <div class="text-14 font-medium whitespace-nowrap text-font-1">
          {{ $t('zong-xiao-hao-yan-bi') }}
        </div>
        <div
          class="leading-[1] font-medium whitespace-nowrap flex items-end mb-[3px]"
        >
          <span class="text-[24px] text-primary">{{ userTotal || 0 }}</span>
        </div>
      </div>
      <div
        class="storage p-[16px] bg-back-1 rounded-[8px] flex flex-col flex-1"
      >
        <div class="text-14 font-medium text-font-1">
          {{ $t('cun-chu-kong-jian-shi-yong') }}
        </div>
        <div class="flex justify-between flex-1 gap-x-[57px]">
          <div class="flex flex-nowrap gap-x-[12px]">
            <div class="leading-[1] flex items-end font-medium">
              <span class="text-primary text-[24px]">
                {{
                  record?.rights?.total_storage
                    ? Number(
                        (
                          ((storage?.total_size || 0) /
                            record?.rights?.total_storage) *
                          100
                        ).toFixed(2)
                      )
                    : 0
                }}
              </span>
              <span class="text-font-60 text-[14px]">%</span>
            </div>
            <div class="text-14 flex items-end font-medium leading-[1]">
              <span class="text-primary">
                {{ getFormateFileSize(storage?.total_size || 0, false) }}
                <!-- {{ (storage?.total_size || 0) / Math.pow(1024, 3) }} -->
              </span>
              <!-- <span class="text-font-60">GB</span> -->
            </div>
          </div>
          <div class="flex flex-col gap-y-[16px]">
            <div class="text-12 text-font-1 flex items-end whitespace-nowrap">
              <span class="text-font-70">{{ $t('cheng-pian') }}：</span>
              <span class="text-font-1">
                {{ getFormateFileSize(storage?.task_size || 0, false) }}
              </span>
            </div>
            <div class="text-12 flex items-end whitespace-nowrap">
              <span class="text-font-70">{{ $t('zi-yuan') }}：</span>
              <span class="text-font-1">
                {{ getFormateFileSize(storage?.media_size || 0, false) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="text-font-70 text-14 font-medium mb-[12px]">
      {{ $t('yan-bi-xiao-hao-ji-lu') }}
    </div>
    <a-table
      ref="tableRef"
      :columns="columns"
      :data-source="dataSource"
      :loading="loading"
      :pagination="pagination"
      :scroll="dataSource?.length ? { y: scrollY } : undefined"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, text, record }">
        <div v-if="!record?.id" class="h-[22px]"></div>
        <div v-else-if="column.key === 'type'">
          {{ useDictItem('object_type', text)?.label }}
        </div>
        <div v-else-if="column.key === 'behavior'">
          {{ record.behavior_name || useDictItem('behavior', text)?.label }}
        </div>
        <div v-else-if="column.key === 'create_time'">
          {{ timeFormat(text, 'YYYY.MM.DD HH:mm:ss') }}
        </div>
        <div v-else-if="column.key === 'credit_coin'">
          {{ text }}
          <span
            v-if="record?.refund_info?.credit_coin"
            class="text-12 text-font-60"
          >
            ({{ $t('yi-tui-hui') }} {{ record.refund_info.credit_coin }})
          </span>
        </div>
      </template>
      <template #emptyText>
        <EmptyView class="h-auto" :class="{ invisible: loading }">
          <template #content> {{ $t('zan-wu-shu-ju') }} </template>
        </EmptyView>
      </template>
    </a-table>
  </div>
</template>
<script lang="ts" setup>
import type { TableProps } from 'ant-design-vue'
import { Icons } from './change'
import FilterDropdown from '@/features/filterDropdown/index.vue'
import { consumption, fetchMembersStorage } from '~/api'
import { usePage } from '~/hooks/usePage'
import { useTime } from '~/hooks/useTime'

const props = defineProps<{
  userId?: number
  record?: any
  scrollY?: string
  pageSize?: number
}>()

const emit = defineEmits(['total'])
const { timeFormat } = useTime()

const tableRef = ref()
const userTotal = ref(0)
const storage = ref<any>({})
const { $t } = useNuxtApp()

const columns: any[] = [
  {
    title: $t('shi-jian'),
    dataIndex: 'create_time',
    key: 'create_time',
    sorter: true,
    defaultSortOrder: 'descend',
    width: 160,
  },
  {
    title: $t('dui-xiang-ming-cheng'),
    dataIndex: 'name',
    key: 'name',
    width: 170,
    filterIcon: Icons.search,
    filterDropdown: (props: any) =>
      h(FilterDropdown, { userId: props.userId, type: 'name', ...props }),
  },
  {
    title: $t('dui-xiang-lei-xing'),
    dataIndex: 'type',
    key: 'type',
    filterIcon: Icons.filter,
    filterDropdown: (props: any) =>
      h(FilterDropdown, {
        userId: props.userId,
        type: 'object_type',
        ...props,
      }),
    width: 120,
  },
  {
    title: $t('cao-zuo-hang-wei'),
    dataIndex: 'behavior',
    key: 'behavior',
    filterIcon: Icons.filter,
    filterDropdown: (props: any) =>
      h(FilterDropdown, {
        userId: props.userId,
        type: 'behavior_group',
        ...props,
      }),
    width: 160,
  },
  {
    title: $t('yan-bi-xiao-hao'),
    dataIndex: 'credit_coin',
    key: 'credit_coin',
    sorter: true,
    width: 120,
  },
]

const { list, total, page, page_size, loading, getList, reload } = usePage({
  api: async (params) => {
    const res: any = await consumption({
      o: '-create_time',
      ...params,
      many: 1,
      user_id: props.userId,
    })
    userTotal.value = res.data?.all_total_used_coins
    emit('total', res.data?.all_total_used_coins)
    return res
  },
  pageSize: props.pageSize || 10,
})

const dataSource = computed(() => {
  if (list.value.length > 0 && list.value.length < page_size.value) {
    const arr = new Array(page_size.value - list.value.length)
    return [...list.value, ...arr]
  }
  return list.value
})

const pagination = computed<any>(() => ({
  total: total.value,
  current: page.value,
  pageSize: page_size.value,
  showTotal: (total: string) => $t('gong-total-tiao', [total]),
  showSizeChanger: true,
  showQuickJumper: true,
}))

const handleTableChange: TableProps['onChange'] = (p, filters, sorter: any) => {
  page.value = p.current as number
  page_size.value = p.pageSize as number
  getList({
    page: p.current,
    page_size: p.pageSize,
    name__icontains: filters.name?.join(','),
    behavior__in: filters.behavior?.join(','),
    type__in: filters.type?.join(','),
    o: sorter.order
      ? `${sorter.order === 'ascend' ? '' : '-'}${sorter?.field}`
      : undefined,
  })
}

const handleFetchStorage = async () => {
  try {
    const responae: any = await fetchMembersStorage({
      many: true,
      user_id: props?.record?.user_id,
    })
    if (!responae?.error_code) {
      storage.value = responae?.data?.[0]
    }
  } catch (error) {
    console.error(error)
  }
}

reload()

onMounted(() => {
  handleFetchStorage()
})
</script>
<style lang="scss" scoped>
:deep(.ant-table-wrapper td.ant-table-column-sort) {
  // background-color: transparent;
}
// :deep(.ant-table-wrapper .ant-table-thead th.ant-table-column-sort) {
//   background-color: #fafafa;
//   &:hover {
//     background-color: #f0f0f0;
//   }
// }

.line {
  @apply flex justify-between flex-nowrap;
}
:deep(.ant-table-placeholder) {
  .ant-table-cell {
    border-width: 0px !important;
  }
}
</style>
